// ABOUTME: Zustand store for game state with level, XP, streaks, and achievements
// ABOUTME: Persists to localStorage and manages gamification progression

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SkinTone, Undertone, Season } from '@/constants/colorPalettes'

export interface ColorProfile {
  skinTone: SkinTone | null
  undertone: Undertone | null
  season: Season | null
  completedAt: Date | null
}

export interface LifestyleProfile {
  workplace: 'office-formal' | 'office-casual' | 'remote' | 'trades' | 'other'
  socialFrequency: 'rarely' | 'sometimes' | 'often'
  exerciseFrequency: 'rarely' | 'sometimes' | 'often'
}

export type BodyType = 'slim' | 'athletic' | 'average' | 'broad'

export interface OnboardingState {
  completed: boolean
  currentStep: number
  welcomeShown: boolean
  setupComplete: boolean
  firstItemScanned: boolean
  firstOutfitCreated: boolean
  skipReason: string | null
  lifestyleProfile: LifestyleProfile | null
  bodyType: BodyType | null
  initialWardrobeScore: number | null
  firstChallengeUnlocked: boolean
}

export interface GameState {
  level: number
  experiencePoints: number
  currentStreak: number
  totalChallengesCompleted: number
  itemsScanned: number
  outfitsCreated: number
  modulesCompleted: number
  perfectQuizzes: number
  colorProfile: ColorProfile
  onboarding: OnboardingState
  unlockedAchievements: string[]
  currentChallenges: Challenge[]
  recentActivity: ActivityItem[]
}

export interface Challenge {
  id: string
  title: string
  description: string
  experienceReward: number
  difficultyLevel: number
  status: 'pending' | 'in_progress' | 'completed'
  progress: number
  maxProgress: number
}

export interface ActivityItem {
  id: string
  type: 'level_up' | 'challenge_completed' | 'achievement_unlocked' | 'xp_gained' | 'outfit_logged'
  message: string
  timestamp: Date
  experienceGained?: number
}

interface GameActions {
  addExperience: (amount: number) => void
  completeChallenge: (challengeId: string) => void
  unlockAchievement: (achievementId: string) => void
  addActivity: (activity: Omit<ActivityItem, 'id' | 'timestamp'>) => void
  incrementItemsScanned: () => void
  incrementOutfitsCreated: () => void
  incrementModulesCompleted: () => void
  incrementPerfectQuizzes: () => void
  setColorProfile: (profile: ColorProfile) => void
  updateOnboarding: (updates: Partial<OnboardingState>) => void
  completeOnboarding: () => void
  skipOnboarding: (reason: string) => void
  resetGame: () => void
}

type GameStore = GameState & GameActions

const EXPERIENCE_PER_LEVEL = 1000
const MAX_ACTIVITY_ITEMS = 50

const initialState: GameState = {
  level: 1,
  experiencePoints: 0,
  currentStreak: 0,
  totalChallengesCompleted: 0,
  itemsScanned: 0,
  outfitsCreated: 0,
  modulesCompleted: 0,
  perfectQuizzes: 0,
  colorProfile: {
    skinTone: null,
    undertone: null,
    season: null,
    completedAt: null,
  },
  onboarding: {
    completed: false,
    currentStep: 0,
    welcomeShown: false,
    setupComplete: false,
    firstItemScanned: false,
    firstOutfitCreated: false,
    skipReason: null,
    lifestyleProfile: null,
    bodyType: null,
    initialWardrobeScore: null,
    firstChallengeUnlocked: false,
  },
  unlockedAchievements: [],
  currentChallenges: [],
  recentActivity: [],
}

export const useGameStore = create<GameStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      addExperience: (amount: number) => {
        set((state) => {
          const newExperience = state.experiencePoints + amount
          const newLevel = Math.max(1, Math.floor(newExperience / EXPERIENCE_PER_LEVEL) + 1)
          const leveledUp = newLevel > state.level

          const newActivity: ActivityItem = {
            id: crypto.randomUUID(),
            type: 'xp_gained',
            message: `Gained ${amount} XP`,
            timestamp: new Date(),
            experienceGained: amount,
          }

          let activities = [...state.recentActivity, newActivity]

          if (leveledUp) {
            const levelUpActivity: ActivityItem = {
              id: crypto.randomUUID(),
              type: 'level_up',
              message: `Leveled up to Level ${newLevel}!`,
              timestamp: new Date(),
            }
            activities.push(levelUpActivity)
          }

          // Keep only the most recent activities
          if (activities.length > MAX_ACTIVITY_ITEMS) {
            activities = activities.slice(-MAX_ACTIVITY_ITEMS)
          }

          return {
            experiencePoints: newExperience,
            level: newLevel,
            recentActivity: activities,
          }
        })
      },

      completeChallenge: (challengeId: string) => {
        // Check if challenge exists and is not already completed
        const challenge = get().currentChallenges.find((c) => c.id === challengeId)
        if (!challenge || challenge.status === 'completed') return

        set((state) => {
          const updatedChallenges = state.currentChallenges.map((c) =>
            c.id === challengeId
              ? { ...c, status: 'completed' as const, progress: c.maxProgress }
              : c
          )

          const newActivity: ActivityItem = {
            id: crypto.randomUUID(),
            type: 'challenge_completed',
            message: `Completed "${challenge.title}"`,
            timestamp: new Date(),
            experienceGained: challenge.experienceReward,
          }

          let activities = [...state.recentActivity, newActivity]
          if (activities.length > MAX_ACTIVITY_ITEMS) {
            activities = activities.slice(-MAX_ACTIVITY_ITEMS)
          }

          return {
            currentChallenges: updatedChallenges,
            totalChallengesCompleted: state.totalChallengesCompleted + 1,
            currentStreak: state.currentStreak + 1,
            recentActivity: activities,
          }
        })

        // Add experience after updating challenge state
        get().addExperience(challenge.experienceReward)
      },

      unlockAchievement: (achievementId: string) => {
        set((state) => {
          if (state.unlockedAchievements.includes(achievementId)) return state

          const newActivity: ActivityItem = {
            id: crypto.randomUUID(),
            type: 'achievement_unlocked',
            message: 'Unlocked new achievement!',
            timestamp: new Date(),
          }

          let activities = [...state.recentActivity, newActivity]
          if (activities.length > MAX_ACTIVITY_ITEMS) {
            activities = activities.slice(-MAX_ACTIVITY_ITEMS)
          }

          return {
            unlockedAchievements: [...state.unlockedAchievements, achievementId],
            recentActivity: activities,
          }
        })
      },

      addActivity: (activity) => {
        set((state) => {
          const newActivity: ActivityItem = {
            ...activity,
            id: crypto.randomUUID(),
            timestamp: new Date(),
          }

          let activities = [...state.recentActivity, newActivity]
          if (activities.length > MAX_ACTIVITY_ITEMS) {
            activities = activities.slice(-MAX_ACTIVITY_ITEMS)
          }

          return { recentActivity: activities }
        })
      },

      incrementItemsScanned: () => {
        set((state) => ({
          itemsScanned: state.itemsScanned + 1,
        }))
      },

      incrementOutfitsCreated: () => {
        set((state) => ({
          outfitsCreated: state.outfitsCreated + 1,
        }))
      },

      incrementModulesCompleted: () => {
        set((state) => ({
          modulesCompleted: state.modulesCompleted + 1,
        }))
      },

      incrementPerfectQuizzes: () => {
        set((state) => ({
          perfectQuizzes: state.perfectQuizzes + 1,
        }))
      },

      setColorProfile: (profile: ColorProfile) => {
        set({ colorProfile: profile })
      },

      updateOnboarding: (updates: Partial<OnboardingState>) => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            ...updates,
          },
        }))
      },

      completeOnboarding: () => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            completed: true,
            setupComplete: true,
          },
        }))
      },

      skipOnboarding: (reason: string) => {
        set((state) => ({
          onboarding: {
            ...state.onboarding,
            completed: true,
            skipReason: reason,
          },
        }))
      },

      resetGame: () => {
        set(initialState)
      },
    }),
    {
      name: 'gentleman-game-store',
      partialize: (state) => ({
        level: state.level,
        experiencePoints: state.experiencePoints,
        currentStreak: state.currentStreak,
        totalChallengesCompleted: state.totalChallengesCompleted,
        itemsScanned: state.itemsScanned,
        outfitsCreated: state.outfitsCreated,
        modulesCompleted: state.modulesCompleted,
        perfectQuizzes: state.perfectQuizzes,
        colorProfile: state.colorProfile,
        onboarding: state.onboarding,
        unlockedAchievements: state.unlockedAchievements,
        recentActivity: state.recentActivity.slice(-10), // Only persist recent activities
      }),
    }
  )
)
